import { BadgeCheck, Users, Film, Zap, ArrowLeft, Eye, Heart, Layers, Box, Play, Mic, Radio, Image as ImageIcon, ExternalLink, ShieldAlert, ShieldOff, Lock } from "lucide-react";
import { Link } from "wouter";
import { useGetCreator, useListMembersContent, useLikeMembersContent } from "@workspace/api-client-react";
import type { MembersContent } from "@workspace/api-client-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ContentOrientationToggle, type ContentOrientation } from "@/components/ContentOrientationToggle";
import { confirmAdultAccess } from "@/components/AdultContentToggle";
import { AdultLockedLink, AdultLockOverlay } from "@/components/AdultLockedLink";

const TIER_STYLES: Record<string, string> = {
  free: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  standard: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  premium: "bg-violet-500/20 text-violet-300 border border-violet-500/30",
};

const MEDIA_ICONS: Record<string, typeof Play> = {
  video: Play, audio: Mic, livestream: Radio,
  image: ImageIcon, photo: ImageIcon,
  series: Layers, virtual: Box,
};

const MEDIA_LABEL: Record<string, string> = {
  video: "Video", audio: "Audio", livestream: "Live",
  image: "Photo", photo: "Photo", series: "Series", virtual: "4D Space",
};

const MEDIA_TYPES = ["all", "photo", "video", "series", "virtual"];

function ContentTile({ item, slug }: { item: MembersContent; slug: string }) {
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(item.likeCount);
  const like = useLikeMembersContent();
  const Icon = MEDIA_ICONS[item.mediaType] ?? Play;
  const tier = TIER_STYLES[item.tier] ?? TIER_STYLES.free;
  const isVideo = item.mediaType === "video" || item.mediaType === "series";

  function handleLike(e: React.MouseEvent) {
    e.preventDefault();
    if (liked) return;
    like.mutate({ id: item.id } as Parameters<typeof like.mutate>[0], {
      onSuccess: (d) => { setLiked(true); setCount(d.likeCount); },
    });
  }

  return (
    <AdultLockedLink item={item} href={`/creators/${slug}/post/${item.id}`} className="group relative rounded-xl overflow-hidden bg-card border border-border hover:border-primary/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10 block">
      <div className="relative aspect-video overflow-hidden">
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-zinc-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        {item.isAdult && <AdultLockOverlay />}
        {/* Play overlay for videos */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider ${tier}`}>{item.tier}</span>
          {item.isAdult && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30 uppercase tracking-wider">18+</span>
          )}
        </div>
        <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="absolute bottom-2 left-2 text-xs text-zinc-300 flex items-center gap-1">
          <Zap className="w-3 h-3 text-primary" />
          <span>{Number(item.gasFeeGlo).toFixed(5)} GLO</span>
        </div>
        {item.durationSeconds && (
          <div className="absolute bottom-2 right-2 text-xs text-zinc-400 bg-black/60 px-1.5 py-0.5 rounded">
            {Math.floor(item.durationSeconds / 60)}:{String(item.durationSeconds % 60).padStart(2, "0")}
          </div>
        )}
      </div>
      <div className="p-3">
        <span className="text-[10px] font-mono text-primary uppercase tracking-widest">{MEDIA_LABEL[item.mediaType] ?? item.mediaType}</span>
        <p className="text-sm font-medium text-white line-clamp-2 leading-snug mt-0.5 mb-2">{item.title}</p>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Eye className="w-3 h-3" /> {(item.viewCount / 1000).toFixed(1)}k
          </span>
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 text-xs transition-colors ${liked ? "text-pink-400" : "text-muted-foreground hover:text-pink-400"}`}
          >
            <Heart className={`w-3 h-3 ${liked ? "fill-pink-400" : ""}`} />
            {count.toLocaleString()}
          </button>
        </div>
      </div>
    </AdultLockedLink>
  );
}

type Props = {
  creatorId: number;
  categoryLabel?: string;
  routeSlug?: string;
  onlyMemberzSlug?: string;
  tags?: string[];
  paywallPriceHalo?: number;
};

export default function CreatorProfile({ creatorId, categoryLabel, routeSlug, onlyMemberzSlug, tags, paywallPriceHalo }: Props) {
  const [filter, setFilter] = useState("all");
  const [showAdult, setShowAdult] = useState(false);
  const [adultConfirmed, setAdultConfirmed] = useState(false);
  const [orientation, setOrientation] = useState<ContentOrientation>("day");

  const { data: creator, isLoading } = useGetCreator(creatorId);
  const { data: allContent } = useListMembersContent({ creatorId, showAdult, orientation });

  const content = filter === "all" ? allContent : allContent?.filter(c => c.mediaType === filter);

  async function handleAdultToggle() {
    if (!showAdult && !adultConfirmed) {
      if (!(await confirmAdultAccess())) return;
      setAdultConfirmed(true);
      setShowAdult(true);
    } else {
      setShowAdult(prev => !prev);
    }
  }

  async function changeOrientation(next: ContentOrientation) {
    setOrientation(next);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Profile not found.
      </div>
    );
  }

  const ogAvatar = creator.avatarUrl?.startsWith("http")
    ? creator.avatarUrl
    : `${window.location.origin}${creator.avatarUrl}`;
  const ogTitle = `${creator.displayName} — ISM | New World`;
  const ogDesc = creator.bio ?? `${creator.displayName} on ISM New World`;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDesc} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:image" content={ogAvatar} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDesc} />
        <meta name="twitter:image" content={ogAvatar} />
      </Helmet>
      {/* Banner */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        {creator.bannerUrl ? (
          <img src={creator.bannerUrl} alt="" className="w-full h-full object-cover opacity-60" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-black" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>

      <div className="container mx-auto px-4 max-w-7xl">
        {/* Avatar + name */}
        <div className="relative -mt-14 mb-6 flex items-end gap-4">
          <div className="relative flex-shrink-0">
            <img
              src={creator.avatarUrl}
              alt={creator.displayName}
              className="w-24 h-24 rounded-full border-4 border-background object-cover ring-2 ring-primary/40"
            />
            {creator.verified && (
              <BadgeCheck className="absolute -bottom-1 -right-1 w-7 h-7 text-primary bg-background rounded-full p-0.5" />
            )}
          </div>
          <div className="pb-2 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-tight">{creator.displayName}</h1>
              <span className="text-xs font-mono text-primary border border-primary/30 px-2 py-0.5 rounded uppercase">
                {categoryLabel ?? creator.category}
              </span>
            </div>
            <p className="text-sm text-muted-foreground font-mono mt-0.5">@{creator.username}</p>
            {creator.walletAddress && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5 break-all">
                {creator.walletAddress.slice(0, 10)}…{creator.walletAddress.slice(-8)}
              </p>
            )}
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map(t => (
                  <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-white/10 text-muted-foreground">{t}</span>
                ))}
              </div>
            )}
          </div>
          {onlyMemberzSlug && (
            <a
              href={`https://only-memberz.com/models/${onlyMemberzSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 text-xs border border-white/10 px-3 py-1.5 rounded-lg text-muted-foreground hover:text-white hover:border-white/30 transition-colors flex-shrink-0"
            >
              <ExternalLink className="w-3 h-3" /> View on Only-Memberz
            </a>
          )}
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap gap-6 mb-6 pb-6 border-b border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="w-4 h-4 text-primary" />
            <span><strong className="text-foreground">{creator.subscriberCount.toLocaleString()}</strong> subscribers</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Film className="w-4 h-4 text-primary" />
            <span><strong className="text-foreground">{creator.contentCount ?? content?.length ?? 0}</strong> posts</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-primary">
            <Zap className="w-4 h-4" />
            <span><strong>{Number(creator.gloEarned ?? 0).toFixed(4)}</strong> GLO earned</span>
          </div>
          <div className="ml-auto flex items-center gap-3 p-3 rounded-lg bg-card border border-border">
            <Zap className="w-4 h-4 text-primary" />
            <div>
              <p className="text-sm font-semibold">
                {Number(creator.subscriptionPriceHalo ?? 0).toFixed(4)} HALO <span className="text-xs text-muted-foreground font-normal">/ month</span>
              </p>
              <p className="text-xs text-muted-foreground">
                ≈ ${(Number(creator.subscriptionPriceHalo ?? 0) * 3.6667).toFixed(2)} USD
              </p>
            </div>
          </div>
        </div>

        {/* Bio */}
        {creator.bio && (
          <p className="text-sm text-muted-foreground mb-8 max-w-2xl leading-relaxed">{creator.bio}</p>
        )}

        {/* Paywall banner */}
        {paywallPriceHalo != null && (
          <div className="relative mb-8 rounded-2xl overflow-hidden border border-amber-500/30">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-950/80 via-zinc-900/90 to-black/95" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
            <div className="relative px-6 py-7 flex flex-col md:flex-row md:items-center gap-6">
              {/* Left: lock icon + messaging */}
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-mono text-amber-400/80 uppercase tracking-widest mb-1">Members Only</p>
                  <h3 className="text-lg font-bold text-white leading-snug">
                    Subscribe to unlock {creator.displayName}'s full collection
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    Exclusive videos, photo sets & live drops — available on Only-Memberz.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {["Premium videos", "Exclusive photo sets", "Live drops", "Early access"].map(f => (
                      <span key={f} className="flex items-center gap-1.5 text-xs text-zinc-300">
                        <span className="w-1 h-1 rounded-full bg-amber-400 inline-block" />
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Right: price + CTA */}
              <div className="flex-shrink-0 flex flex-col items-start md:items-end gap-3">
                <div>
                  <p className="text-3xl font-black text-white leading-none">
                    {paywallPriceHalo.toFixed(2)}{" "}
                    <span className="text-amber-400 text-2xl">HALO</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5 text-right">
                    ≈ ${(paywallPriceHalo * 3.6667).toFixed(2)} USD / month
                  </p>
                </div>
                <a
                  href={`https://only-memberz.com/models/${onlyMemberzSlug ?? creator.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition-colors shadow-lg shadow-amber-500/20"
                >
                  <Zap className="w-4 h-4" />
                  Subscribe on Only-Memberz
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <ContentOrientationToggle value={orientation} onChange={changeOrientation} />
        </div>

        {/* Media filter + 18+ toggle */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {MEDIA_TYPES.map(t => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors ${
                filter === t
                  ? "bg-primary border-primary text-white"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
              }`}
            >
              {t === "virtual" ? "4D / Virtual" : t === "all" ? "All Content" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}

          <div className="ml-auto">
            <button
              onClick={handleAdultToggle}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                showAdult
                  ? "bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
              }`}
              title={showAdult ? "Hide 18+ content" : "Show 18+ content (adult)"}
            >
              {showAdult ? (
                <><ShieldOff className="w-3.5 h-3.5" /> 18+ On</>
              ) : (
                <><ShieldAlert className="w-3.5 h-3.5" /> 18+ Off</>
              )}
            </button>
          </div>
        </div>

        {/* 18+ disclaimer banner */}
        {showAdult && (
          <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
            <ShieldAlert className="w-4 h-4 flex-shrink-0" />
            <span>Adult content is now visible. You must be 18+ to view this material. Content is sourced from Only-Memberz.</span>
          </div>
        )}

        {/* Content grid */}
        {content && content.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-16">
            {content.map(item => <ContentTile key={item.id} item={item} slug={routeSlug ?? onlyMemberzSlug ?? String(creatorId)} />)}
          </div>
        ) : (
          <div className="text-center py-20 text-muted-foreground text-sm">
            {showAdult ? "No content in this category yet." : "No content in this category yet."}
          </div>
        )}

        {/* Footer links */}
        <div className="pb-8 flex items-center gap-4">
          <Link href="/creators" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All Creators
          </Link>
          {onlyMemberzSlug && (
            <a
              href={`https://only-memberz.com/models/${onlyMemberzSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="md:hidden inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> View on Only-Memberz
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
